// apps/spa/src/routes/ReminderEdit.tsx
// /reminders/:id/edit — fetches the reminder via useReminder(id), converts
// the wire shape to the form shape (intToHex on color, cronToPreset on
// scheduleCron, JSON.parse on embedJson), then mounts <ReminderEditorForm
// mode='edit' />.
//
// While loading: Skeleton placeholder.
// On 404: 'Reminder not found' + back link (the api wrapper throws ApiError
// with status=404 here — useReminder's query.error.status is checked).

import { useMemo } from 'react';
import { Link, useParams } from 'react-router';
import { ChevronLeft } from 'lucide-react';
import { BASTN_GOLD, type Reminder } from '@guardian/shared';
import { Skeleton } from '@/components/ui/skeleton';
import { ApiError } from '@/lib/api';
import { intToHex } from '@/lib/color';
import { cronToPreset, type SchedulePreset } from '@/lib/schedule';
import {
  ReminderEditorForm,
  type ReminderFormValues,
} from '@/components/reminders/ReminderEditorForm';
import { useReminder } from '@/hooks/useReminders';

function reminderToFormValues(r: Reminder): ReminderFormValues {
  // Parse stored embed and translate the int color back to hex string.
  let parsedEmbed: Record<string, unknown>;
  try {
    parsedEmbed = JSON.parse(r.embedJson) as Record<string, unknown>;
  } catch {
    parsedEmbed = {};
  }

  const colorInt =
    typeof parsedEmbed.color === 'number'
      ? (parsedEmbed.color as number)
      : BASTN_GOLD;

  // cronToPreset returns either a SchedulePreset or { type: 'custom', cron }.
  // Custom cron expressions can't render in the segmented control — fall back
  // to manual so the form still mounts. (The save path will preserve the
  // existing cron only if the user doesn't touch the schedule editor; for now
  // operators editing a custom cron via the SPA collapse it to manual or
  // rebuild via Daily/Weekly. CONTEXT explicitly defers custom-cron UI.)
  const cronResolution = cronToPreset(r.scheduleCron);
  const schedulePreset: SchedulePreset =
    cronResolution.type === 'custom'
      ? { type: 'manual' }
      : cronResolution;

  return {
    name: r.name,
    channelId: r.channelId,
    schedulePreset,
    embed: {
      title: typeof parsedEmbed.title === 'string' ? parsedEmbed.title : '',
      description:
        typeof parsedEmbed.description === 'string'
          ? parsedEmbed.description
          : '',
      url: typeof parsedEmbed.url === 'string' ? parsedEmbed.url : '',
      color: intToHex(colorInt),
      footer: {
        text:
          typeof (parsedEmbed.footer as { text?: string } | undefined)?.text === 'string'
            ? ((parsedEmbed.footer as { text?: string }).text as string)
            : '',
        icon_url:
          typeof (parsedEmbed.footer as { icon_url?: string } | undefined)?.icon_url === 'string'
            ? ((parsedEmbed.footer as { icon_url?: string }).icon_url as string)
            : '',
      },
      thumbnail: {
        url:
          typeof (parsedEmbed.thumbnail as { url?: string } | undefined)?.url === 'string'
            ? ((parsedEmbed.thumbnail as { url?: string }).url as string)
            : '',
      },
      image: {
        url:
          typeof (parsedEmbed.image as { url?: string } | undefined)?.url === 'string'
            ? ((parsedEmbed.image as { url?: string }).url as string)
            : '',
      },
    },
  };
}

export function ReminderEdit() {
  const { id } = useParams<{ id: string }>();
  const reminderQuery = useReminder(id);

  const formValues = useMemo(
    () =>
      reminderQuery.data ? reminderToFormValues(reminderQuery.data) : undefined,
    [reminderQuery.data],
  );

  if (reminderQuery.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (reminderQuery.error) {
    const apiError =
      reminderQuery.error instanceof ApiError
        ? reminderQuery.error
        : null;
    if (apiError && apiError.status === 404) {
      return (
        <div className="space-y-4 py-8 text-center">
          <h2 className="font-display text-2xl">Reminder not found</h2>
          <p className="text-muted-foreground text-sm">
            This reminder may have been deleted by another officer.
          </p>
          <Link
            to="/reminders"
            className="text-foreground inline-flex items-center text-sm underline"
          >
            <ChevronLeft className="size-4" /> Back to reminders
          </Link>
        </div>
      );
    }
    // Other errors (e.g. 500) fall through to a generic error block.
    return (
      <div className="space-y-4 py-8 text-center">
        <h2 className="font-display text-2xl">Could not load reminder</h2>
        <Link
          to="/reminders"
          className="text-foreground inline-flex items-center text-sm underline"
        >
          <ChevronLeft className="size-4" /> Back to reminders
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Link
        to="/reminders"
        className="text-muted-foreground hover:text-foreground inline-flex items-center text-sm transition-colors"
      >
        <ChevronLeft className="size-4" />
        Back to reminders
      </Link>
      <ReminderEditorForm
        mode="edit"
        reminderId={id}
        defaultValues={formValues}
        // Phase 5 (Plan 05-02): pass auto-disabled state so the form renders the
        // recovery notice. Server-side enforces the recovery on Save regardless,
        // but the UI needs to surface the state to the officer up front.
        wasAutoDisabled={reminderQuery.data?.disabledReason === 'auto_failure'}
      />
    </div>
  );
}
